import { Fit, RowStack } from "@skbkontur/react-stack-layout";
import { Button, Input, Modal, Select } from "@skbkontur/react-ui";
import * as React from "react";

import {
    AccountInfo,
    AccountType,
    Address,
    AddressForeign,
    ClientInfo,
    EmployeeInfo,
    Gender,
    IndividualStatus,
    RecipientInfo,
    SenderInfo,
    SignerType,
} from "../../Domain/ClientInfo/FormsClientInfo";

import cn from "./ClientInfoEditor.less";
import { RecursiveRequired, ValueBind } from "./ControlLenses";
import { LensFactory, useLensFactory, ValueBindWithInfo } from "./ReactControlLenses";

const accountTypeItems: Array<[AccountType, string]> = [
    [AccountType.Organization, "Organization"],
    [AccountType.Individual, "Individual"],
    [AccountType.ForeignOrganization, "ForeignOrganization"],
    [AccountType.CreditOrganization, "CreditOrganization"],
];

const genderItems: Array<[Gender, string]> = [
    [Gender.Unknown, "Unknown"],
    [Gender.Male, "Male"],
    [Gender.Female, "Female"],
];

const individualStatusItems: Array<[IndividualStatus, string]> = [
    [IndividualStatus.Unknown, "Unknown"],
    [IndividualStatus.Resident, "Resident"],
    [IndividualStatus.NonResident, "NonResident"],
];

const signerTypeItems: Array<[SignerType, string]> = [
    [SignerType.Unknown, "Unknown"],
    [SignerType.Chief, "Chief"],
    [SignerType.Representative, "Representative"],
];

interface ClientInfoEditorProps {
    value: ClientInfo;
    onValueChange: (nextValue: ClientInfo) => void;
}

interface EditorLineProps {
    caption: string;
    children: React.ReactNode;
}

function EditorItem(props: EditorLineProps): JSX.Element {
    return (
        <div className={cn("editor-item")}>
            <div className={cn("caption")}>{props.caption}</div>
            <div className={cn("value")}>{props.children}</div>
        </div>
    );
}

interface EditorLineStringProps {
    caption?: string;
    skipTokens?: number;
    lens: ValueBindWithInfo<string>;
}

function EditorLineString(props: EditorLineStringProps): JSX.Element {
    return (
        <EditorItem caption={props.lens.pathTokens.slice(props.skipTokens ?? 0).join(".")}>
            <Input {...props.lens} width={200} />
        </EditorItem>
    );
}

interface EditorLineModalProps<T> {
    caption?: string;
    skipTokens?: number;
    lens: ValueBindWithInfo<T>;
    children: (lensFactory: LensFactory<T>) => React.ReactNode;
}

function EditorLineModal<T>(props: EditorLineModalProps<T>): JSX.Element {
    const [modalEditorVisible, setModalEditorVisible] = React.useState<boolean>(false);
    const [modalValue, setModalValue] = React.useState<T>(() => props.lens.value);
    const lensFactory = useLensFactory(modalValue, setModalValue);

    return (
        <EditorItem caption={props.caption ?? props.lens.pathTokens.slice(props.skipTokens ?? 0).join(".")}>
            <Button
                width={200}
                onClick={() => {
                    setModalValue(props.lens.value);
                    setModalEditorVisible(true);
                }}>
                Изменить...
            </Button>
            {modalEditorVisible && (
                <Modal
                    ignoreBackgroundClick
                    onClose={() => {
                        setModalEditorVisible(false);
                    }}>
                    <Modal.Header>
                        {props.caption ??
                            props.lens.pathTokens.slice(props.skipTokens ? props.skipTokens - 1 : 0).join(".")}
                    </Modal.Header>
                    <Modal.Body>
                        <div style={{ width: 630 }}>{props.children(lensFactory)}</div>
                    </Modal.Body>
                    <Modal.Footer>
                        <RowStack gap={2} baseline>
                            <Fit>
                                <Button
                                    use={"primary"}
                                    onClick={() => {
                                        props.lens.onValueChange(modalValue);
                                        setModalEditorVisible(false);
                                    }}>
                                    Сохранить
                                </Button>
                            </Fit>
                            <Fit>
                                <Button
                                    onClick={() => {
                                        setModalEditorVisible(false);
                                    }}>
                                    Отмена
                                </Button>
                            </Fit>
                        </RowStack>
                    </Modal.Footer>
                </Modal>
            )}
        </EditorItem>
    );
}

export function ClientInfoEditor({ value, onValueChange }: ClientInfoEditorProps): JSX.Element {
    const lens = useLensFactory(value, onValueChange);
    return (
        <div style={{ width: 650 }}>
            <section>
                <h3>Current account</h3>
                <AccountInfoEditor {...lens.to(x => x.CurrentAccount)} />
                <h3>Sender</h3>
                <SenderInfoEditor {...lens.to(x => x.Sender)} />
                <h3>Recipient</h3>
                <RecipientInfoEditor {...lens.to(x => x.Recipient)} />
            </section>
        </div>
    );
}

export function RecipientInfoEditor({
    value,
    onValueChange,
}: ValueBind<RecursiveRequired<RecipientInfo>>): JSX.Element {
    const lens = useLensFactory(value, onValueChange);

    return (
        <div className={cn("editor-items")}>
            <EditorLineString lens={lens.to(x => x.IfnsCode)} />
            <EditorLineString lens={lens.to(x => x.MriCode)} />
            <EditorLineString lens={lens.to(x => x.TogsCode)} />
            <EditorLineString lens={lens.to(x => x.UpfrCode)} />
            <EditorLineString lens={lens.to(x => x.PrototypeUpfrCode)} />
        </div>
    );
}

export function SenderInfoEditor({ value, onValueChange }: ValueBind<RecursiveRequired<SenderInfo>>): JSX.Element {
    const lens = useLensFactory(value, onValueChange);

    return (
        <div className={cn("editor-items")}>
            <EditorLineString lens={lens.to(x => x.Inn)} />
            <EditorLineString lens={lens.to(x => x.Kpp)} />
            <EditorLineString lens={lens.to(x => x.FullName)} />
            <EditorItem caption={"Type"}>
                <Select<AccountType, string> width={200} items={accountTypeItems} {...lens.to(x => x.Type)} />
            </EditorItem>
            <EditorItem caption={"Type"}>
                <Select<SignerType, string> width={200} items={signerTypeItems} {...lens.to(x => x.Signer.Type)} />
            </EditorItem>
            <EditorLineString lens={lens.to(x => x.Signer.Certificate.Inn)} />
            <EditorLineString lens={lens.to(x => x.Signer.Certificate.Fio.Surname)} />
            <EditorLineString lens={lens.to(x => x.Signer.Certificate.Fio.Name)} />
            <EditorLineString lens={lens.to(x => x.Signer.Certificate.Fio.Patronymic)} />
            <EditorLineString lens={lens.to(x => x.Signer.Certificate.Ogrn)} />
            <EditorLineString lens={lens.to(x => x.Signer.Certificate.OgrnIp)} />
            <EditorLineString lens={lens.to(x => x.Signer.Certificate.OrgName)} />
            <EditorLineString lens={lens.to(x => x.Signer.Certificate.RnsFss)} />
            <EditorLineString lens={lens.to(x => x.Signer.Certificate.Region)} />
        </div>
    );
}

export function AccountInfoEditor({ value, onValueChange }: ValueBind<RecursiveRequired<AccountInfo>>): JSX.Element {
    const lens = useLensFactory(value, onValueChange);

    return (
        <>
            <div className={cn("editor-items")}>
                <EditorItem caption={"Type"}>
                    <Select<AccountType, string> width={200} items={accountTypeItems} {...lens.to(x => x.Type)} />
                </EditorItem>
                <EditorLineString lens={lens.to(x => x.Inn)} />
                <EditorLineString lens={lens.to(x => x.FullName)} />
                <EditorLineString lens={lens.to(x => x.ShortName)} />
                <EditorLineString lens={lens.to(x => x.Ogrn)} />
                <EditorLineString lens={lens.to(x => x.Okato)} />
                <EditorLineString lens={lens.to(x => x.Oktmo)} />
                <EditorLineString lens={lens.to(x => x.Okved)} />
                <EditorLineString lens={lens.to(x => x.Okpo)} />
                <EditorLineString lens={lens.to(x => x.Phone)} />
                <EditorLineModal lens={lens.to(x => x.AddressRegistration)}>
                    {modalLens => <AddressEditor {...modalLens.current()} />}
                </EditorLineModal>
                <EditorLineModal lens={lens.to(x => x.AddressActual)}>
                    {modalLens => <AddressEditor {...modalLens.current()} />}
                </EditorLineModal>
                <EditorLineModal lens={lens.to(x => x.Chief)}>
                    {modalLens => <EmployeeInfoEditor {...modalLens.current()} />}
                </EditorLineModal>
            </div>
            <hr />
            <h4>Organization</h4>
            <div className={cn("editor-items")}>
                <EditorLineString skipTokens={1} lens={lens.to(x => x.Organization.Kpp)} />
                <EditorLineString skipTokens={1} lens={lens.to(x => x.Organization.KppObject)} />
                <EditorLineString skipTokens={1} lens={lens.to(x => x.Organization.KppAnother)} />
                <EditorLineString skipTokens={1} lens={lens.to(x => x.Organization.Okopf)} />
                <EditorLineString skipTokens={1} lens={lens.to(x => x.Organization.Okfs)} />
                <EditorLineModal skipTokens={1} lens={lens.to(x => x.Organization.Bookkeeper)}>
                    {modalLens => <EmployeeInfoEditor {...modalLens.current()} />}
                </EditorLineModal>
                <EditorLineString skipTokens={1} lens={lens.to(x => x.Organization.SeparateDepartment.Kpp)} />
                <EditorLineString skipTokens={1} lens={lens.to(x => x.Organization.SeparateDepartment.Phone)} />
                <EditorLineString skipTokens={1} lens={lens.to(x => x.Organization.SeparateDepartment.FullName)} />
                <EditorLineString
                    skipTokens={1}
                    lens={lens.to(x => x.Organization.SeparateDepartment.DepartmentCode)}
                />
                <EditorLineModal skipTokens={1} lens={lens.to(x => x.Organization.SeparateDepartment.AddressSD)}>
                    {modalLens => <AddressEditor {...modalLens.current()} />}
                </EditorLineModal>
                <EditorLineString skipTokens={1} lens={lens.to(x => x.Organization.CreditOrganization.Bik)} />
                <EditorLineString
                    skipTokens={1}
                    lens={lens.to(x => x.Organization.CreditOrganization.RegistrationNumber)}
                />
                <EditorLineModal skipTokens={1} lens={lens.to(x => x.Organization.ForeignOrganization.AddressForeign)}>
                    {modalLens => <AddressForeignEditor {...modalLens.current()} />}
                </EditorLineModal>
            </div>
            <h4>Individual</h4>
            <div className={cn("editor-items")}>
                <EditorLineString skipTokens={1} lens={lens.to(x => x.Individual.PolicyNumber)} />
                <EditorItem caption={"Status"}>
                    <Select<IndividualStatus, string>
                        width={200}
                        items={individualStatusItems}
                        {...lens.to(x => x.Individual.Status)}
                    />
                </EditorItem>
            </div>
            <h4>Fns</h4>
            <div className={cn("editor-items")}>
                <EditorLineString skipTokens={1} lens={lens.to(x => x.Fns.RepresentativeDocument.Name)} />
                <EditorLineModal skipTokens={1} lens={lens.to(x => x.Fns.Representative)}>
                    {modalLens => <EmployeeInfoEditor {...modalLens.current()} />}
                </EditorLineModal>
            </div>

            <h4>Fss</h4>
            <div className={cn("editor-items")}>
                <EditorLineString skipTokens={1} lens={lens.to(x => x.Fss.RegistrationNumber)} />
                <EditorLineString skipTokens={1} lens={lens.to(x => x.Fss.AdditionalCode)} />
                <EditorLineString skipTokens={1} lens={lens.to(x => x.Fss.DependencyCode)} />
                <EditorLineModal skipTokens={1} lens={lens.to(x => x.Fss.Representative)}>
                    {modalLens => <EmployeeInfoEditor {...modalLens.current()} />}
                </EditorLineModal>
            </div>

            <h4>Pfr</h4>
            <div className={cn("editor-items")}>
                <EditorLineString skipTokens={1} lens={lens.to(x => x.Pfr.CategoryCode)} />
                <EditorLineString skipTokens={1} lens={lens.to(x => x.Pfr.RegistrationNumber)} />
                <EditorLineString skipTokens={1} lens={lens.to(x => x.Pfr.RegistrationNumberIp)} />
            </div>

            <h4>Oms</h4>
            <div className={cn("editor-items")}>
                <EditorLineString skipTokens={1} lens={lens.to(x => x.Oms.Tfoms)} />
            </div>
        </>
    );
}

function EmployeeInfoEditor(props: ValueBind<RecursiveRequired<EmployeeInfo>>): JSX.Element {
    const lens = useLensFactory(props.value, props.onValueChange);

    return (
        <div className={cn("editor-items")}>
            <EditorLineString lens={lens.to(x => x.Fio.Surname)} />
            <EditorLineString lens={lens.to(x => x.Fio.Name)} />
            <EditorLineString lens={lens.to(x => x.Fio.Patronymic)} />
            <EditorLineString lens={lens.to(x => x.Inn)} />
            <EditorLineString lens={lens.to(x => x.Position)} />
            <EditorLineString lens={lens.to(x => x.Phone)} />
            <EditorLineString lens={lens.to(x => x.Email)} />
            <EditorLineString lens={lens.to(x => x.BirthDate)} />
            <EditorLineString lens={lens.to(x => x.BirthPlace)} />
            <EditorItem caption={"Gender"}>
                <Select<Gender, string> width={200} items={genderItems} {...lens.to(x => x.Gender)} />
            </EditorItem>
            <EditorLineString lens={lens.to(x => x.CitizenshipCode)} />
            <EditorLineString lens={lens.to(x => x.Passport.SeriesNumber)} />
            <EditorLineString lens={lens.to(x => x.Passport.Code)} />
            <EditorLineString lens={lens.to(x => x.Passport.IssuedBy)} />
            <EditorLineString lens={lens.to(x => x.Passport.IssuedDate)} />
            <EditorLineModal lens={lens.to(x => x.AddressRegistration)}>
                {modalLens => <AddressEditor {...modalLens.current()} />}
            </EditorLineModal>
            <EditorLineModal lens={lens.to(x => x.AddressForeign)}>
                {modalLens => <AddressForeignEditor {...modalLens.current()} />}
            </EditorLineModal>
        </div>
    );
}

function AddressEditor(props: ValueBind<RecursiveRequired<Address>>): JSX.Element {
    const lens = useLensFactory(props.value, props.onValueChange);

    return (
        <div className={cn("editor-items")}>
            <EditorLineString lens={lens.to(x => x.Country)} />
            <EditorLineString lens={lens.to(x => x.Index)} />
            <EditorLineString lens={lens.to(x => x.Region.Name)} />
            <EditorLineString lens={lens.to(x => x.District.Name)} />
            <EditorLineString lens={lens.to(x => x.City.Name)} />
            <EditorLineString lens={lens.to(x => x.Settlement.Name)} />
            <EditorLineString lens={lens.to(x => x.Street.Name)} />
            <EditorLineString lens={lens.to(x => x.HouseRange.Name)} />
            <EditorLineString lens={lens.to(x => x.House)} />
            <EditorLineString lens={lens.to(x => x.Building)} />
            <EditorLineString lens={lens.to(x => x.Flat)} />
        </div>
    );
}

function AddressForeignEditor(props: ValueBind<RecursiveRequired<AddressForeign>>): JSX.Element {
    const lens = useLensFactory(props.value, props.onValueChange);

    return (
        <div className={cn("editor-items")}>
            <EditorLineString lens={lens.to(x => x.Country)} />
            <EditorLineString lens={lens.to(x => x.CountryCode)} />
            <EditorLineString lens={lens.to(x => x.Index)} />
            <EditorLineString lens={lens.to(x => x.Address)} />
        </div>
    );
}
